import React, {
  useCallback, useContext, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import useMobileLayout from 'Hooks/useMobileLayout';

// The lower you make this, the more likely it is that a user accidentally closes the popper they see when they hover over a dropdown.
// The higher you make this, the more likely it is that the popper stays open unnecessarily, potentially blocking the user from seeing content they care about
const POPPER_CLOSE_DEBOUNCE_MS = 100;

const NavButtonsContext = React.createContext(undefined);

export function NavButtonsContextProvider({ children }) {
  const [btnWithHover, setBtnWithHover] = useState();
  const dropdownCloseTimerRef = useRef(null);

  return (
    <NavButtonsContext.Provider value={{ btnWithHover, setBtnWithHover, dropdownCloseTimerRef }}>
      {children}
    </NavButtonsContext.Provider>
  );
}

NavButtonsContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export const useNavButtonsContext = (id) => {
  const { btnWithHover, setBtnWithHover, dropdownCloseTimerRef } = useContext(NavButtonsContext);
  const isMobile = useMobileLayout();
  const debounceMs = isMobile ? 0 : POPPER_CLOSE_DEBOUNCE_MS;

  const onOpen = useCallback(() => {
    setBtnWithHover(id);
    dropdownCloseTimerRef.current = null;
  }, [setBtnWithHover, id]);

  // If another button has hover, let it continue to have hover. But if this button has hover, it should no longer have it
  const actuallyClose = useCallback(() => setBtnWithHover((newBtnWithHover) => {
    if (newBtnWithHover === id) return undefined;
    return newBtnWithHover;
  }), [setBtnWithHover]);

  // onClose is effectively debounced, except that the debounced call can be cancelled by calling onOpen
  const onClose = useCallback(() => {
    const myTimer = setTimeout(() => {
      if (dropdownCloseTimerRef.current === myTimer) { // For example, the timer could have been reset by briefly hovering over the same element again
        actuallyClose();
      }
    }, debounceMs);
    dropdownCloseTimerRef.current = myTimer;
  }, [dropdownCloseTimerRef, actuallyClose, debounceMs]);

  return {
    onOpen,
    onClose,
    onToggle: btnWithHover === id ? onClose : onOpen,
    onNavClick: actuallyClose,
    dropdownOpen: btnWithHover === id,
  };
};
